import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { request } from "@/api/client";
import {
  fetchAgentUsage,
  fetchExecutiveAIUsage,
  fetchExecutiveActivity,
  fetchExecutiveMetrics,
  fetchExecutiveOrganizations,
  fetchSystemUsers,
  type AgentUsageMap,
  type ExecutiveAIUsage,
  type ExecutiveActivityEvent,
  type ExecutiveMetrics,
  type MasterControlOrganization,
  type MasterControlUser,
  type CapabilityDebugPayload,
} from "@/api/executive";
import { fetchAccountingHealth, type AccountingHealth } from "@/api/accounting";
import { useToast } from "@/components/ToastProvider";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useEventStream, type SSEEvent } from "@/hooks/useEventStream";

type SystemHealthPayload = (Record<string, unknown> & {
  status?: string;
  version?: string;
  uptime?: unknown;
  services?: Record<string, Record<string, unknown>>;
  redis?: Record<string, unknown>;
  chroma?: Record<string, unknown>;
  postgres?: Record<string, unknown>;
  workflow?: Record<string, unknown>;
  event_stream?: Record<string, unknown>;
  system?: {
    uptime_seconds?: number | null;
    memory?: {
      total_bytes?: number;
      available_bytes?: number;
      used_bytes?: number;
      percent_used?: number;
    };
    swap?: {
      total_bytes?: number;
      free_bytes?: number;
      used_bytes?: number;
      percent_used?: number;
    };
    storage?: Array<Record<string, unknown>>;
    vpn?: Record<string, unknown>;
  };
}) | null;

export type MasterControlHealth = {
  core: SystemHealthPayload;
  accounting: AccountingHealth | null;
};

export type MasterControlDerived = {
  dau: number;
  mau: number;
  activeOrgs: number;
  userById: Record<string, MasterControlUser>;
  orgById: Record<string, MasterControlOrganization>;
};

export type MasterControlAIUsage = ExecutiveAIUsage | null;

export type MasterControlErrorEntry = ExecutiveActivityEvent;

export type MasterControlDangerActions = {
  deleteUser: (userId: string) => Promise<void>;
  deleteOrganization: (orgId: string) => Promise<void>;
  clearRedisQueues: () => Promise<void>;
  purgeAgentMemory: (agentId: string) => Promise<void>;
  resetWorkflowEngine: () => Promise<void>;
  triggerGlobalResync: () => Promise<void>;
};

export type MasterControlUserDryRun = {
  exists: boolean;
  user?: {
    id: string;
    email?: string | null;
    name?: string | null;
    role?: string | null;
    org_id?: string | null;
  } | null;
  related: {
    events: number;
    agent_actions: number;
    memory_vectors: number;
  };
};

export type MasterControlOrgDryRun = {
  exists: boolean;
  organization?: {
    id: string;
    name?: string | null;
    created_at?: string | null;
  } | null;
  members: Array<{
    id: string;
    email?: string | null;
    name?: string | null;
    role?: string | null;
  }>;
  related: {
    venues: number;
    crm_contacts: number;
    tasks: number;
  };
};

export type MasterControlRedisDryRun = {
  queues: Record<string, number>;
};

export type MasterControlChromaDryRun = {
  collection: string;
  vector_count: number;
};

export type MasterControlWorkflowDryRun = {
  active_workflows: number;
  stalled: number;
  pending_tasks: number;
};

export type MasterControlSyncDryRun = {
  spotify_pending: number;
  press_pending: number;
  bit_pending: number;
  venues_pending: number;
};

export type MasterControlDryRun = {
  userDeletion: (userId: string) => Promise<MasterControlUserDryRun>;
  organizationDeletion: (orgId: string) => Promise<MasterControlOrgDryRun>;
  redis: () => Promise<MasterControlRedisDryRun>;
  chroma: (agentId: string) => Promise<MasterControlChromaDryRun>;
  workflows: () => Promise<MasterControlWorkflowDryRun>;
  sync: () => Promise<MasterControlSyncDryRun>;
};

export type MasterControlWorkflowEntry = {
  workflow_id: string;
  agent: string;
  status: "active" | "stalled" | "pending";
  last_event_at: string | null;
  run_time_seconds: number | null;
  stalled_for_seconds: number | null;
  pending_actions: number;
  raw: Record<string, unknown>;
};

export type MasterControlWorkflowState = {
  active: MasterControlWorkflowEntry[];
  stalled: MasterControlWorkflowEntry[];
  pendingTasks: MasterControlWorkflowEntry[];
  stats: Record<string, number>;
} | null;

export type MasterControlIngestionPipeline = {
  id: string;
  label: string;
  pending: number;
  last_run: string | null;
  last_error?: string | null;
  status: "ok" | "warning" | "error";
  metadata?: Record<string, unknown> | null;
};

export type MasterControlIngestionState = MasterControlIngestionPipeline[] | null;

export type MasterControlSettings = {
  enableMarketingAgent: boolean;
  enablePublicistAgent: boolean;
  enableAutoSync: boolean;
  ingestionFrequencyMinutes: number;
  maxWorkflowConcurrency: number;
};

export type MasterControlPerformance = {
  databaseLatencyMs: number;
  redisLatencyMs: number;
  chromaLatencyMs: number;
  cacheHitRatio: number;
  workflowThroughput: number;
  ingestionThroughput: number;
};

export type MasterControlAlertSettings = {
  slackEnabled: boolean;
  slackWebhook: string | null;
  emailEnabled: boolean;
  emailAddress: string | null;
  webhookEnabled: boolean;
  webhookUrl: string | null;
};

export type MasterControlPromptAtlasEntry = {
  id: string;
  label: string;
  category: string;
  agent: string;
  module: string;
  attribute: string;
  filePath: string;
  prompt: string;
  defaultPrompt: string;
  hasOverride: boolean;
  description?: string | null;
};

export type MasterContactRegistryEntry = {
  id: string;
  venue_name: string;
  normalized_name: string;
  domain?: string | null;
  location_city?: string | null;
  location_region?: string | null;
  location_country?: string | null;
  booking_email?: string | null;
  contact_url?: string | null;
  source_url?: string | null;
  confidence_score?: number | null;
  confidence_reasons?: string[];
  tags?: string[];
  notes?: string | null;
  last_verified_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type MasterContactRegistryUpsert = {
  venue_name: string;
  domain?: string | null;
  location_city?: string | null;
  location_region?: string | null;
  location_country?: string | null;
  booking_email?: string | null;
  contact_url?: string | null;
  source_url?: string | null;
  confidence_score?: number | null;
  confidence_reasons?: string[];
  tags?: string[];
  notes?: string | null;
  last_verified_at?: string | null;
};

export type UseMasterControlResult = {
  users: MasterControlUser[];
  organizations: MasterControlOrganization[];
  agentUsage: AgentUsageMap;
  metrics: ExecutiveMetrics;
  health: MasterControlHealth;
  errors: MasterControlErrorEntry[];
  auditLogs: MasterControlAuditLog[];
  workflowState: MasterControlWorkflowState;
  ingestionState: MasterControlIngestionState;
  settingsState: MasterControlSettings | null;
  performanceState: MasterControlPerformance | null;
  alertSettings: MasterControlAlertSettings | null;
  capabilityProfile: CapabilityDebugPayload | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  fetchAuditLogs: (limit?: number) => Promise<void>;
  dryRun: MasterControlDryRun;
  fetchWorkflowState: () => Promise<void>;
  fetchIngestionState: () => Promise<void>;
  fetchSettings: () => Promise<void>;
  saveSettings: (next: MasterControlSettings) => Promise<void>;
  fetchPerformanceState: () => Promise<void>;
  fetchAlertSettings: () => Promise<void>;
  saveAlertSettings: (next: MasterControlAlertSettings) => Promise<void>;
  sendTestAlert: () => Promise<void>;
  fetchCapabilityProfile: () => Promise<void>;
  executeDangerousAction: (endpoint: string, options?: RequestInit & { body?: unknown }) => Promise<any>;
  runGlobalSync: () => Promise<void>;
  derived: MasterControlDerived;
  dangerActions: MasterControlDangerActions;
  eventsConnected: boolean;
  promptAtlas: MasterControlPromptAtlasEntry[];
  fetchPromptAtlas: () => Promise<void>;
  updatePromptAtlasEntry: (promptId: string, prompt: string) => Promise<void>;
  resetPromptAtlasEntry: (promptId: string) => Promise<void>;
  masterContactRegistry: MasterContactRegistryEntry[];
  fetchMasterContactRegistry: (query?: string, limit?: number, offset?: number) => Promise<void>;
  upsertMasterContactRegistry: (payload: MasterContactRegistryUpsert) => Promise<MasterContactRegistryEntry | null>;
  updateMasterContactRegistry: (id: string, payload: MasterContactRegistryUpsert) => Promise<MasterContactRegistryEntry | null>;
  deleteMasterContactRegistry: (id: string) => Promise<void>;
};

const REFRESH_EVENTS = new Set([
  "thread_update",
  "workflow_transition",
  "hitl_pending",
  "autopilot_trigger",
  "delegation",
  "memory_episode_added",
  "artist_bootstrap_progress",
  "artist_ingest_progress",
  "system_error",
  "agent_error",
  "workflow_error",
]);

function normalizeActivityEntry(event: ExecutiveActivityEvent, fallbackId: string): ExecutiveActivityEvent {
  const timestamp = event.timestamp ?? event.created_at ?? new Date().toISOString();
  return {
    ...event,
    timestamp,
    id: event.id ?? fallbackId,
  };
}

function isErrorEvent(event: ExecutiveActivityEvent): boolean {
  const level = (event.level ?? "").toString().toLowerCase();
  if (level.includes("error") || level.includes("critical") || level.includes("fail")) {
    return true;
  }
  const type = (event.type ?? event.event ?? "").toString().toLowerCase();
  if (type.includes("error") || type.includes("exception") || type.includes("fail")) {
    return true;
  }
  const message = (event.message ?? "").toString().toLowerCase();
  return Boolean(message) && (message.includes("error") || message.includes("exception") || message.includes("fail"));
}

function parseTimestamp(value?: string | null): number {
  if (!value) return 0;
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? 0 : ts;
}

function normalizeActivityFeed(events: ExecutiveActivityEvent[]): ExecutiveActivityEvent[] {
  return events
    .map((event, index) => normalizeActivityEntry(event, `activity-${index}`))
    .filter(isErrorEvent)
    .sort((a, b) => parseTimestamp(b.timestamp ?? b.created_at ?? null) - parseTimestamp(a.timestamp ?? a.created_at ?? null))
    .slice(0, 50);
}

async function fetchSystemHealth(): Promise<SystemHealthPayload> {
  const payload = await request<SystemHealthPayload>("/health/system");
  return payload ?? null;
}

function countRecentUsers(users: MasterControlUser[], maxDays: number): number {
  const now = Date.now();
  const thresholdMs = maxDays * 24 * 60 * 60 * 1000;
  return users.filter((user) => {
    const timestamp = user.last_seen_at ?? user.last_login_at ?? null;
    if (!timestamp) return false;
    const parsed = Date.parse(timestamp);
    if (Number.isNaN(parsed)) return false;
    return now - parsed <= thresholdMs;
  }).length;
}

function normalizeWorkflowEntry(
  raw: Record<string, any>,
  status: "active" | "stalled" | "pending",
): MasterControlWorkflowEntry {
  const now = Date.now();
  const workflowId = String(raw.id ?? raw.workflow ?? raw.reference_id ?? raw.domain ?? "workflow");
  const agent = String(raw.workflow ?? raw.domain ?? raw.reference_id ?? "agent");
  const lastEventAt: string | null = raw.last_event?.created_at ?? raw.updated_at ?? raw.last_checkpoint ?? null;
  const createdAt = raw.created_at ? Date.parse(raw.created_at) : NaN;
  const startedAt = raw.started_at ? Date.parse(raw.started_at) : NaN;
  const completedAt = raw.completed_at ? Date.parse(raw.completed_at) : NaN;
  const explicitRuntime =
    raw.run_time_seconds ??
    raw.runtime_seconds ??
    raw.run_time ??
    raw.duration_seconds ??
    null;
  const lastEventTs = lastEventAt ? Date.parse(lastEventAt) : NaN;
  const startTs = Number.isFinite(startedAt) ? startedAt : Number.isFinite(createdAt) ? createdAt : lastEventTs;
  const endTs = Number.isFinite(completedAt)
    ? completedAt
    : Number.isFinite(lastEventTs)
      ? lastEventTs
      : now;
  const computedRuntime =
    Number.isFinite(startTs) && Number.isFinite(endTs) ? Math.max(0, Math.round((endTs - startTs) / 1000)) : null;
  const runTimeSeconds =
    typeof explicitRuntime === "number" && Number.isFinite(explicitRuntime)
      ? Math.max(0, Math.round(explicitRuntime))
      : computedRuntime;
  const stalledSeconds =
    status === "stalled" && Number.isFinite(lastEventTs) ? Math.max(0, Math.round((now - lastEventTs) / 1000)) : null;
  return {
    workflow_id: workflowId,
    agent,
    status,
    last_event_at: lastEventAt,
    run_time_seconds: runTimeSeconds,
    stalled_for_seconds: stalledSeconds,
    pending_actions: status === "pending" ? 1 : raw.awaiting_human_input ? 1 : 0,
    raw,
  };
}

function normalizePromptAtlasEntry(raw: Record<string, any>): MasterControlPromptAtlasEntry {
  return {
    id: String(raw.id ?? raw.agent ?? "agent"),
    label: raw.label ?? raw.id ?? "Agent",
    category: raw.category ?? "General",
    agent: String(raw.agent ?? raw.id ?? ""),
    module: raw.module ?? "",
    attribute: raw.attribute ?? "",
    filePath: raw.file_path ?? raw.filePath ?? "",
    prompt: raw.prompt ?? "",
    defaultPrompt: raw.default_prompt ?? raw.defaultPrompt ?? "",
    hasOverride: Boolean(raw.has_override ?? raw.hasOverride),
    description: raw.description ?? null,
  };
}

export function useMasterControl(autoRefreshMs = 60000): UseMasterControlResult {
  const [users, setUsers] = useState<MasterControlUser[]>([]);
  const [organizations, setOrganizations] = useState<MasterControlOrganization[]>([]);
  const [agentUsage, setAgentUsage] = useState<AgentUsageMap>({});
  const [metrics, setMetrics] = useState<ExecutiveMetrics>(null);
  const [aiUsage, setAiUsage] = useState<MasterControlAIUsage>(null);
  const [health, setHealth] = useState<MasterControlHealth>({ core: null, accounting: null });
  const [errors, setErrors] = useState<ExecutiveActivityEvent[]>([]);
  const [auditLogs, setAuditLogs] = useState<MasterControlAuditLog[]>([]);
  const [workflowState, setWorkflowState] = useState<MasterControlWorkflowState>(null);
  const [ingestionState, setIngestionState] = useState<MasterControlIngestionState>(null);
  const [settings, setSettings] = useState<MasterControlSettings | null>(null);
  const [performanceState, setPerformanceState] = useState<MasterControlPerformance | null>(null);
  const [alertSettings, setAlertSettings] = useState<MasterControlAlertSettings | null>(null);
  const [promptAtlas, setPromptAtlas] = useState<MasterControlPromptAtlasEntry[]>([]);
  const [masterContactRegistry, setMasterContactRegistry] = useState<MasterContactRegistryEntry[]>([]);
  const [capabilityProfile, setCapabilityProfile] = useState<CapabilityDebugPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { push } = useToast();
  const { capabilities, hasFullAccess, ready } = useCapabilities();
  const lastErrorRef = useRef<string | null>(null);
  const refreshTriggerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCapability = useCallback(
    (flag?: string) => {
      if (!flag) return true;
      if (hasFullAccess) return true;
      if (!ready) return false;
      return Boolean((capabilities as Record<string, unknown> | undefined)?.[flag]);
    },
    [capabilities, hasFullAccess, ready],
  );

  const fetchAuditLogs = useCallback(
    async (limit = 200) => {
      const payload = await request<{ logs?: MasterControlAuditLog[] }>(
        `/master-control/audit/logs?limit=${encodeURIComponent(limit)}`,
      );
      setAuditLogs(Array.isArray(payload?.logs) ? payload.logs : []);
    },
    [],
  );

  const fetchUserDryRun = useCallback(
    async (userId: string) => {
      return request<MasterControlUserDryRun>(`/master-control/dry-run/users/${encodeURIComponent(userId)}`);
    },
    [],
  );

  const fetchOrgDryRun = useCallback(
    async (orgId: string) => {
      return request<MasterControlOrgDryRun>(`/master-control/dry-run/organizations/${encodeURIComponent(orgId)}`);
    },
    [],
  );

  const fetchRedisDryRun = useCallback(async () => {
    return request<MasterControlRedisDryRun>("/master-control/dry-run/redis");
  }, []);

  const fetchChromaDryRun = useCallback(async (agent: string) => {
    return request<MasterControlChromaDryRun>(`/master-control/dry-run/chroma/${encodeURIComponent(agent)}`);
  }, []);

  const fetchWorkflowDryRun = useCallback(async () => {
    return request<MasterControlWorkflowDryRun>("/master-control/dry-run/workflows");
  }, []);

  const fetchSyncDryRun = useCallback(async () => {
    return request<MasterControlSyncDryRun>("/master-control/dry-run/sync");
  }, []);

  const fetchWorkflowState = useCallback(async () => {
    const payload = await request<{
      active?: Record<string, any>[];
      stalled?: Record<string, any>[];
      pending_tasks?: Record<string, any>[];
      stats?: Record<string, number>;
    }>("/master-control/workflows/state");

    const activeEntries = Array.isArray(payload?.active)
      ? payload.active.map((run) => normalizeWorkflowEntry(run, "active"))
      : [];
    const stalledEntries = Array.isArray(payload?.stalled)
      ? payload.stalled.map((run) => normalizeWorkflowEntry(run, "stalled"))
      : [];
    const pendingEntries = Array.isArray(payload?.pending_tasks)
      ? payload.pending_tasks.map((task) => normalizeWorkflowEntry(task, "pending"))
      : [];
    const stats = payload?.stats ?? {
      total_runs: activeEntries.length + stalledEntries.length,
      active_runs: activeEntries.length,
      stalled_runs: stalledEntries.length,
      pending_tasks: pendingEntries.length,
    };
    setWorkflowState({
      active: activeEntries,
      stalled: stalledEntries,
      pendingTasks: pendingEntries,
      stats,
    });
  }, []);

  const fetchIngestionState = useCallback(async () => {
    const payload = await request<{ pipelines?: Record<string, any>[] | Record<string, any> }>(
      "/master-control/ingestion/status",
    );
    const convert = (item: Record<string, any>, fallbackId: string): MasterControlIngestionPipeline => ({
      id: item.id ?? item.name ?? fallbackId,
      label: item.label ?? item.name ?? fallbackId,
      pending: item.pending ?? 0,
      last_run: item.last_run ?? null,
      last_error: item.last_error ?? null,
      status: (item.status ?? (item.last_error ? "error" : "ok")) as MasterControlIngestionPipeline["status"],
      metadata: item.metadata ?? null,
    });

    let pipelines: MasterControlIngestionPipeline[] = [];
    if (Array.isArray(payload?.pipelines)) {
      pipelines = payload.pipelines.map((item, index) => convert(item, `pipeline-${index + 1}`));
    } else if (payload?.pipelines && typeof payload.pipelines === "object") {
      pipelines = Object.entries(payload.pipelines).map(([key, value]) => convert(value as Record<string, any>, key));
    }
    setIngestionState(pipelines);
  }, []);

  const normalizeSettings = useCallback((payload?: Record<string, any> | null): MasterControlSettings => {
    return {
      enableMarketingAgent: Boolean(payload?.enable_marketing_agent ?? payload?.enableMarketingAgent),
      enablePublicistAgent: Boolean(payload?.enable_publicist_agent ?? payload?.enablePublicistAgent),
      enableAutoSync: Boolean(payload?.enable_auto_sync ?? payload?.enableAutoSync),
      ingestionFrequencyMinutes: Number(payload?.ingestion_frequency_minutes ?? payload?.ingestionFrequencyMinutes ?? 60),
      maxWorkflowConcurrency: Number(payload?.max_workflow_concurrency ?? payload?.maxWorkflowConcurrency ?? 5),
    };
  }, []);

  const fetchSettings = useCallback(async () => {
    const payload = await request<Record<string, any> | null>("/master-control/settings");
    setSettings(normalizeSettings(payload ?? {}));
  }, [normalizeSettings]);

  const saveSettings = useCallback(
    async (next: MasterControlSettings) => {
      await request("/master-control/settings", {
        method: "POST",
        body: {
          enable_marketing_agent: next.enableMarketingAgent,
          enable_publicist_agent: next.enablePublicistAgent,
          enable_auto_sync: next.enableAutoSync,
          ingestion_frequency_minutes: next.ingestionFrequencyMinutes,
          max_workflow_concurrency: next.maxWorkflowConcurrency,
        },
      });
      await fetchSettings();
      push({ title: "Settings saved", message: "Master Control configuration updated." });
    },
    [fetchSettings, push],
  );

  const normalizePerformance = useCallback((payload?: Record<string, any> | null): MasterControlPerformance => {
    return {
      databaseLatencyMs: Number(payload?.databaseLatencyMs ?? payload?.database_latency_ms ?? 0),
      redisLatencyMs: Number(payload?.redisLatencyMs ?? payload?.redis_latency_ms ?? 0),
      chromaLatencyMs: Number(payload?.chromaLatencyMs ?? payload?.chroma_latency_ms ?? 0),
      cacheHitRatio: Number(payload?.cacheHitRatio ?? payload?.cache_hit_ratio ?? 0),
      workflowThroughput: Number(payload?.workflowThroughput ?? payload?.workflow_throughput ?? 0),
      ingestionThroughput: Number(payload?.ingestionThroughput ?? payload?.ingestion_throughput ?? 0),
    };
  }, []);

  const fetchPerformanceState = useCallback(async () => {
    const payload = await request<Record<string, any> | null>("/master-control/performance");
    setPerformanceState(normalizePerformance(payload ?? {}));
  }, [normalizePerformance]);

  const normalizeAlertSettings = useCallback((payload?: Record<string, any> | null): MasterControlAlertSettings => {
    return {
      slackEnabled: Boolean(payload?.slack_enabled ?? payload?.slackEnabled),
      slackWebhook: payload?.slack_webhook ?? payload?.slackWebhook ?? null,
      emailEnabled: Boolean(payload?.email_enabled ?? payload?.emailEnabled),
      emailAddress: payload?.email_address ?? payload?.emailAddress ?? null,
      webhookEnabled: Boolean(payload?.webhook_enabled ?? payload?.webhookEnabled),
      webhookUrl: payload?.webhook_url ?? payload?.webhookUrl ?? null,
    };
  }, []);

  const fetchAlertSettings = useCallback(async () => {
    const payload = await request<Record<string, any> | null>("/master-control/alerts/settings");
    setAlertSettings(normalizeAlertSettings(payload ?? {}));
  }, [normalizeAlertSettings]);

  const saveAlertSettings = useCallback(
    async (next: MasterControlAlertSettings) => {
      await request("/master-control/alerts/settings", {
        method: "POST",
        body: {
          slack_enabled: next.slackEnabled,
          slack_webhook: next.slackWebhook,
          email_enabled: next.emailEnabled,
          email_address: next.emailAddress,
          webhook_enabled: next.webhookEnabled,
          webhook_url: next.webhookUrl,
        },
      });
      await fetchAlertSettings();
      push({ title: "Alerts updated", message: "Notification destinations saved." });
    },
    [fetchAlertSettings, push],
  );

  const sendTestAlert = useCallback(async () => {
    try {
      await request("/master-control/alerts/test", { method: "POST" });
      push({ title: "Test alert sent", message: "Check your configured destinations." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to send test alert.";
      push({ title: "Test alert failed", message });
      throw err instanceof Error ? err : new Error(message);
    }
  }, [push]);

  const fetchCapabilityProfile = useCallback(async () => {
    try {
      const payload = await request<CapabilityDebugPayload>("/master-control/capabilities");
      setCapabilityProfile(payload ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to load capability profile.";
      push({ title: "Capability profile", message });
    }
  }, [push]);

  const fetchPromptAtlas = useCallback(async () => {
    const payload = await request<Record<string, any>[] | null>("/master-control/prompts");
    const entries = Array.isArray(payload) ? payload.map((item) => normalizePromptAtlasEntry(item)) : [];
    setPromptAtlas(entries);
  }, []);

  const fetchMasterContactRegistry = useCallback(
    async (query?: string, limit = 200, offset = 0) => {
      const params = new URLSearchParams();
      if (query) params.set("query", query);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      const payload = await request<{ entries?: MasterContactRegistryEntry[] }>(
        `/master-control/contact-registry?${params.toString()}`,
      );
      setMasterContactRegistry(Array.isArray(payload?.entries) ? payload.entries : []);
    },
    [],
  );

  const upsertMasterContactRegistry = useCallback(
    async (payload: MasterContactRegistryUpsert) => {
      const response = await request<MasterContactRegistryEntry>("/master-control/contact-registry", {
        method: "POST",
        body: payload,
      });
      await fetchMasterContactRegistry();
      return response ?? null;
    },
    [fetchMasterContactRegistry],
  );

  const updateMasterContactRegistry = useCallback(
    async (id: string, payload: MasterContactRegistryUpsert) => {
      const response = await request<MasterContactRegistryEntry>(
        `/master-control/contact-registry/${encodeURIComponent(id)}`,
        { method: "PUT", body: payload },
      );
      await fetchMasterContactRegistry();
      return response ?? null;
    },
    [fetchMasterContactRegistry],
  );

  const deleteMasterContactRegistry = useCallback(
    async (id: string) => {
      await request(`/master-control/contact-registry/${encodeURIComponent(id)}`, { method: "DELETE" });
      await fetchMasterContactRegistry();
    },
    [fetchMasterContactRegistry],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    const canViewSystemStats = hasCapability("canViewSystemStats");
    const canViewAgentUsage = hasCapability("canViewAgentUsage");
    const canViewErrorLog = hasCapability("canViewErrorLog");
    const canViewAuditLog = hasCapability("canViewAuditLog");
    const canTriggerIngestionJobs = hasCapability("canTriggerIngestionJobs");
    const canModifyGlobalSettings = hasCapability("canModifyGlobalSettings");
    const canViewSystemHealth = hasCapability("canViewSystemHealth");

    const fetchIf = <T,>(allowed: boolean, fn: () => Promise<T>) => {
      if (!allowed) {
        return Promise.resolve({ skipped: true, value: undefined as T });
      }
      return fn().then((value) => ({ skipped: false, value }));
    };

    const [
      usersResult,
      orgsResult,
      usageResult,
      aiUsageResult,
      metricsResult,
      activityResult,
      auditLogsResult,
      ingestionResult,
      settingsResult,
      performanceResult,
      alertSettingsResult,
      systemHealthResult,
      accountingHealthResult,
      capabilityResult,
      promptAtlasResult,
    ] = await Promise.allSettled([
      fetchIf(true, fetchSystemUsers),
      fetchIf(true, fetchExecutiveOrganizations),
      fetchIf(canViewAgentUsage, fetchAgentUsage),
      fetchIf(canViewSystemStats, fetchExecutiveAIUsage),
      fetchIf(canViewSystemStats, fetchExecutiveMetrics),
      fetchIf(canViewErrorLog, () => fetchExecutiveActivity(200)),
      fetchIf(canViewAuditLog, fetchAuditLogs),
      fetchIf(canTriggerIngestionJobs, fetchIngestionState),
      fetchIf(canModifyGlobalSettings, fetchSettings),
      fetchIf(canViewSystemHealth, fetchPerformanceState),
      fetchIf(canModifyGlobalSettings, fetchAlertSettings),
      fetchIf(canViewSystemHealth, fetchSystemHealth),
      fetchIf(canViewSystemHealth, fetchAccountingHealth),
      fetchIf(true, () => request<CapabilityDebugPayload>("/master-control/capabilities")),
      fetchIf(canModifyGlobalSettings, fetchPromptAtlas),
    ]);

    const failures: string[] = [];

    if (usersResult.status === "fulfilled" && !usersResult.value.skipped) {
      setUsers(usersResult.value.value);
    } else {
      failures.push("Users");
    }

    if (orgsResult.status === "fulfilled" && !orgsResult.value.skipped) {
      setOrganizations(orgsResult.value.value);
    } else {
      failures.push("Organizations");
    }

    if (usageResult.status === "fulfilled" && !usageResult.value.skipped) {
      setAgentUsage(usageResult.value.value);
    } else {
      if (usageResult.status === "rejected") {
        failures.push("Agent usage");
      }
    }

    if (aiUsageResult.status === "fulfilled" && !aiUsageResult.value.skipped) {
      setAiUsage(aiUsageResult.value.value);
    } else {
      if (aiUsageResult.status === "rejected") {
        failures.push("AI usage");
      }
    }

    if (metricsResult.status === "fulfilled" && !metricsResult.value.skipped) {
      setMetrics(metricsResult.value.value);
    } else {
      if (metricsResult.status === "rejected") {
        failures.push("Metrics");
      }
    }

    if (activityResult.status === "fulfilled" && !activityResult.value.skipped) {
      setErrors(normalizeActivityFeed(activityResult.value.value));
    } else {
      if (activityResult.status === "rejected") {
        failures.push("Activity feed");
      }
    }

    if (auditLogsResult.status !== "fulfilled") {
      failures.push("Audit logs");
    }

    if (ingestionResult.status === "rejected") {
      failures.push("Ingestion state");
    }
    if (settingsResult.status === "rejected") {
      failures.push("Settings");
    }
    if (performanceResult.status === "rejected") {
      failures.push("Performance indicators");
    }
    if (alertSettingsResult.status === "rejected") {
      failures.push("Alert settings");
    }

    setHealth((prev) => ({
      core:
        systemHealthResult.status === "fulfilled" && !systemHealthResult.value.skipped
          ? systemHealthResult.value.value
          : prev.core,
      accounting:
        accountingHealthResult.status === "fulfilled" && !accountingHealthResult.value.skipped
          ? accountingHealthResult.value.value
          : prev.accounting,
    }));

    if (systemHealthResult.status === "rejected") {
      failures.push("System health");
    }
    if (accountingHealthResult.status === "rejected") {
      failures.push("Accounting health");
    }
    if (capabilityResult.status === "fulfilled" && !capabilityResult.value.skipped) {
      setCapabilityProfile(capabilityResult.value.value ?? null);
    } else {
      failures.push("Capability profile");
    }
    if (promptAtlasResult.status === "rejected") {
      failures.push("Prompt atlas");
    }

    if (failures.length) {
      const message = `Failed to refresh: ${failures.join(", ")}`;
      setError(message);
      if (lastErrorRef.current !== message) {
        push({ title: "Master Control", message });
        lastErrorRef.current = message;
      }
    } else {
      setError(null);
      lastErrorRef.current = null;
    }
    setLoading(false);
  }, [
    fetchAuditLogs,
    fetchAlertSettings,
    fetchIngestionState,
    fetchPromptAtlas,
    fetchSettings,
    hasCapability,
    push,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!autoRefreshMs) {
      return;
    }
    const intervalId = setInterval(() => {
      void refresh();
    }, autoRefreshMs);
    return () => clearInterval(intervalId);
  }, [autoRefreshMs, refresh]);

  useEffect(() => {
    if (!hasCapability("canViewWorkflowInspector")) {
      return;
    }
    void fetchWorkflowState().catch(() => {
      /* handled via toast elsewhere */
    });
    const intervalId = setInterval(() => {
      void fetchWorkflowState().catch(() => {
        /* handled via toast elsewhere */
      });
    }, 45000);
    return () => clearInterval(intervalId);
  }, [fetchWorkflowState, hasCapability]);

  useEffect(() => {
    if (!hasCapability("canTriggerIngestionJobs")) {
      return;
    }
    void fetchIngestionState().catch(() => {
      /* handled via toast elsewhere */
    });
    const intervalId = setInterval(() => {
      void fetchIngestionState().catch(() => {
        /* handled elsewhere */
      });
    }, 40000);
    return () => clearInterval(intervalId);
  }, [fetchIngestionState, hasCapability]);

  useEffect(() => {
    if (!hasCapability("canViewSystemHealth")) {
      return;
    }
    void fetchPerformanceState().catch(() => {
      /* handled via toast */
    });
    const intervalId = setInterval(() => {
      void fetchPerformanceState().catch(() => {
        /* handled */
      });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchPerformanceState, hasCapability]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTriggerRef.current) return;
    refreshTriggerRef.current = setTimeout(() => {
      refreshTriggerRef.current = null;
      void refresh();
    }, 2000);
  }, [refresh]);

  useEffect(() => {
    return () => {
      if (refreshTriggerRef.current) {
        clearTimeout(refreshTriggerRef.current);
        refreshTriggerRef.current = null;
      }
    };
  }, []);

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      if (event?.event && REFRESH_EVENTS.has(event.event)) {
        scheduleRefresh();
        if (event.event.startsWith("workflow") || event.event.includes("workflow")) {
          if (hasCapability("canViewWorkflowInspector")) {
            void fetchWorkflowState().catch(() => {
              /* no-op */
            });
          }
        }
        if (event.event.includes("sync") || event.event.includes("ingestion")) {
          if (hasCapability("canTriggerIngestionJobs")) {
            void fetchIngestionState().catch(() => {
              /* no-op */
            });
          }
        }
      }
      if (!event?.data || typeof event.data !== "object") {
        return;
      }
      const normalized = normalizeActivityEntry(event.data as ExecutiveActivityEvent, `sse-${Date.now()}`);
      if (!isErrorEvent(normalized)) return;
      setErrors((prev) => {
        const filtered = normalized.id ? prev.filter((entry) => entry.id !== normalized.id) : prev;
        return [normalized, ...filtered].slice(0, 50);
      });
    },
    [fetchIngestionState, fetchWorkflowState, hasCapability, scheduleRefresh],
  );

  const { connected: eventsConnected } = useEventStream(handleEvent);

  const dryRun = useMemo<MasterControlDryRun>(
    () => ({
      userDeletion: fetchUserDryRun,
      organizationDeletion: fetchOrgDryRun,
      redis: fetchRedisDryRun,
      chroma: fetchChromaDryRun,
      workflows: fetchWorkflowDryRun,
      sync: fetchSyncDryRun,
    }),
    [fetchChromaDryRun, fetchOrgDryRun, fetchRedisDryRun, fetchSyncDryRun, fetchUserDryRun, fetchWorkflowDryRun],
  );

  const derived = useMemo<MasterControlDerived>(() => {
    const dauMetric = metrics?.dau ?? metrics?.daily_active_users ?? null;
    const mauMetric = metrics?.mau ?? metrics?.monthly_active_users ?? null;
    const dau = typeof dauMetric === "number" ? dauMetric : countRecentUsers(users, 1);
    const mau = typeof mauMetric === "number" ? mauMetric : countRecentUsers(users, 30);
    const activeOrgsMetric = metrics?.active_organizations ?? null;
    const activeOrgs =
      typeof activeOrgsMetric === "number"
        ? activeOrgsMetric
        : organizations.filter((org) => (org.status ?? "").toString().toLowerCase() === "active").length;
    const userById = users.reduce<Record<string, MasterControlUser>>((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});
    const orgById = organizations.reduce<Record<string, MasterControlOrganization>>((acc, org) => {
      acc[org.id] = org;
      return acc;
    }, {});
    return { dau, mau, activeOrgs, userById, orgById };
  }, [metrics, organizations, users]);

  const executeDangerousAction = useCallback(
    async (endpoint: string, options?: RequestInit & { body?: unknown }) => {
      return request(endpoint, {
        ...options,
        headers: {
          ...(options?.headers ?? {}),
          "X-MC-Confirm": "yes",
        },
      });
    },
    [],
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      const trimmed = userId.trim();
      if (!trimmed) {
        throw new Error("User identifier required.");
      }
      try {
        await executeDangerousAction(`/master-control/users/${encodeURIComponent(trimmed)}`, { method: "DELETE" });
        push({ title: "User deleted", message: trimmed });
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to delete user";
        push({ title: "Action failed", message });
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [executeDangerousAction, push, refresh],
  );

  const deleteOrganization = useCallback(
    async (orgId: string) => {
      const trimmed = orgId.trim();
      if (!trimmed) {
        throw new Error("Organization identifier required.");
      }
      try {
        await executeDangerousAction(`/master-control/organizations/${encodeURIComponent(trimmed)}`, {
          method: "DELETE",
        });
        push({ title: "Organization deleted", message: trimmed });
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to delete organization";
        push({ title: "Action failed", message });
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [executeDangerousAction, push, refresh],
  );

  const clearRedisQueues = useCallback(async () => {
    try {
      await executeDangerousAction("/master-control/maintenance/redis/clear", { method: "POST" });
      push({ title: "Redis queues cleared", message: "Dispatch queues flushed." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to clear Redis queues";
      push({ title: "Action failed", message });
      throw err instanceof Error ? err : new Error(message);
    }
  }, [executeDangerousAction, push]);

  const purgeAgentMemory = useCallback(
    async (agentId: string) => {
      const trimmed = agentId.trim();
      if (!trimmed) {
        throw new Error("Agent identifier required.");
      }
      try {
        await executeDangerousAction(`/master-control/maintenance/chroma/purge/${encodeURIComponent(trimmed)}`, {
          method: "POST",
        });
        push({ title: "Chroma purge dispatched", message: trimmed });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to purge agent memory";
        push({ title: "Action failed", message });
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [executeDangerousAction, push],
  );

  const resetWorkflowEngine = useCallback(async () => {
    try {
      await executeDangerousAction("/master-control/maintenance/workflows/reset", { method: "POST" });
      push({ title: "Workflow engine reset", message: "Orchestrator restarted." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to reset workflow engine";
      push({ title: "Action failed", message });
      throw err instanceof Error ? err : new Error(message);
    }
  }, [executeDangerousAction, push]);

  const triggerGlobalResync = useCallback(async () => {
    try {
      await executeDangerousAction("/master-control/maintenance/sync/all", { method: "POST" });
      push({ title: "Resync triggered", message: "Global sync started." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to trigger resync";
      push({ title: "Action failed", message });
      throw err instanceof Error ? err : new Error(message);
    }
  }, [executeDangerousAction, push]);

  const runGlobalSync = useCallback(async () => {
    await triggerGlobalResync();
    await fetchIngestionState();
  }, [fetchIngestionState, triggerGlobalResync]);

  const updatePromptAtlasEntry = useCallback(
    async (promptId: string, prompt: string) => {
      await request(`/master-control/prompts/${encodeURIComponent(promptId)}`, {
        method: "PUT",
        body: { prompt },
      });
      await fetchPromptAtlas();
      push({ title: "Prompt saved", message: `${promptId} prompt updated.` });
    },
    [fetchPromptAtlas, push],
  );

  const resetPromptAtlasEntry = useCallback(
    async (promptId: string) => {
      await request(`/master-control/prompts/${encodeURIComponent(promptId)}`, { method: "DELETE" });
      await fetchPromptAtlas();
      push({ title: "Prompt reset", message: `${promptId} reverted to default.` });
    },
    [fetchPromptAtlas, push],
  );

  const dangerActions = useMemo<MasterControlDangerActions>(
    () => ({
      deleteUser,
      deleteOrganization,
      clearRedisQueues,
      purgeAgentMemory,
      resetWorkflowEngine,
      triggerGlobalResync,
    }),
    [clearRedisQueues, deleteOrganization, deleteUser, purgeAgentMemory, resetWorkflowEngine, triggerGlobalResync],
  );

  return {
    users,
    organizations,
    agentUsage,
    aiUsage,
    metrics,
    health,
    errors,
    auditLogs,
    workflowState,
    ingestionState,
    settingsState: settings,
    performanceState,
    alertSettings,
    capabilityProfile,
    promptAtlas,
    loading,
    error,
    refresh,
    fetchAuditLogs,
    fetchWorkflowState,
    fetchIngestionState,
    fetchSettings,
    saveSettings,
    fetchPerformanceState,
    fetchAlertSettings,
    saveAlertSettings,
    sendTestAlert,
    fetchCapabilityProfile,
    fetchPromptAtlas,
    updatePromptAtlasEntry,
    resetPromptAtlasEntry,
    masterContactRegistry,
    fetchMasterContactRegistry,
    upsertMasterContactRegistry,
    updateMasterContactRegistry,
    deleteMasterContactRegistry,
    executeDangerousAction,
    runGlobalSync,
    derived,
    dangerActions,
    dryRun,
    eventsConnected,
  };
}

export default useMasterControl;
